// client/components/ui/StatusFilter.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { STATUS_VALUES, MANGEL_STATUS_VALUES, OFFERTE_STATUS_VALUES } from "../../constants/status";
import { listSortTriggerClassName, filterDrawerTriggerClassName } from "../../design/tokens.js";
import { cn } from "../../utils/themeClasses.js";
import AppButton from "./AppButton.jsx";
import { Z_INDEX } from "./zIndex.js";
import { useFilterDrawerMode } from "./filterDrawerContext.js";
import { computeFixedPopoverPosition } from "../../utils/fixedPopoverPosition.js";

/**
 * StatusFilter
 * Props:
 *  - entity: 'maengel' | 'generic' (Default: generic)
 *  - value: '__all__' | string
 *  - onChange: (newValue) => void
 *  - labelAll?: string
 */
export default function StatusFilter({
  entity = "generic",
  value,
  onChange,
  labelAll = "Alle Status",
  exclude = [],
  optionsOverride = null,
  triggerClassName = "",
}) {
    const inFilterDrawer = useFilterDrawerMode();
  const excludeSet = useMemo(() => new Set(exclude.map(s => String(s).toLowerCase())), [exclude]);
  const menuWidth = 260;
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuWrapRef = useRef(null);
  const [pos, setPos] = useState({
    top: 0,
    left: 0,
    width: menuWidth
  });
  const options = useMemo(() => {
    if (Array.isArray(optionsOverride) && optionsOverride.length) {
      return optionsOverride.map(opt => {
        if (opt && typeof opt === "object") {
          return {
            value: opt.value,
            label: opt.label ?? String(opt.value ?? "")
          };
        }
        return {
          value: opt,
          label: String(opt ?? "")
        };
      }).filter(opt => String(opt.value ?? "").length > 0);
    }
    let base = STATUS_VALUES;
    if (entity === "maengel") base = MANGEL_STATUS_VALUES;
    if (entity === "offerte") base = OFFERTE_STATUS_VALUES;
    return base.filter(s => !excludeSet.has(String(s).toLowerCase())).map(s => ({
      value: s,
      label: s
    }));
  }, [entity, excludeSet, optionsOverride]);
  const currentLabel = value && value !== "__all__" ? options.find(o => String(o.value).toLowerCase() === String(value).toLowerCase())?.label || String(value) : labelAll;
  const toggleMenu = () => {
    if (inFilterDrawer) {
      setOpen(v => !v);
      return;
    }
    openMenu();
  };
  const openMenu = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const estimate = Math.min(60 + options.length * 44, 360);
    setPos(computeFixedPopoverPosition(r, { width: menuWidth, heightEstimate: estimate }));
    setOpen(true);
  };
  const pick = val => {
    onChange?.(val);
    setOpen(false);
  };
  useEffect(() => {
    if (!open) return;
    const onKey = e => e.key === "Escape" && setOpen(false);
    const onClick = e => {
      if (inFilterDrawer) return;
      if (!menuWrapRef.current) return;
      if (!menuWrapRef.current.contains(e.target) && !btnRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    const onRecalc = () => {
      if (!inFilterDrawer) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onClick, true);
    window.addEventListener("resize", onRecalc, { passive: true });
    window.addEventListener("scroll", onRecalc, { passive: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onClick, true);
      window.removeEventListener("resize", onRecalc);
      window.removeEventListener("scroll", onRecalc);
    };
  }, [open, inFilterDrawer]);
  const menuPanel = (
    <div role="listbox" aria-label="Status wählen" className={inFilterDrawer ? "max-h-[min(40dvh,280px)] overflow-auto" : "max-h-[60vh] overflow-auto"}>
      <MenuItem active={value === "__all__"} label={labelAll} onClick={() => pick("__all__")} />
      <div className={`my-1 h-px ${"bg-border-muted"}`} />
      {options.map(({
        value: v,
        label
      }) => <MenuItem key={v} active={String(value).toLowerCase() === String(v).toLowerCase()} label={label} onClick={() => pick(v)} />)}
    </div>
  );

  return inFilterDrawer ? (
    <div className="w-full min-w-0">
      <AppButton ref={btnRef} onClick={toggleMenu} type="button" variant="secondary" className={cn(listSortTriggerClassName, filterDrawerTriggerClassName, triggerClassName)} aria-haspopup="listbox" aria-expanded={open} title="Status filtern">
        {currentLabel}
      </AppButton>
      {open ? (
        <div className="mt-1.5 w-full rounded-xl popover-panel p-1 border border-default">
          {menuPanel}
        </div>
      ) : null}
    </div>
  ) : (
    <>
      <AppButton ref={btnRef} onClick={toggleMenu} type="button" variant="secondary" className={cn(listSortTriggerClassName, "text-sm", triggerClassName)} aria-haspopup="listbox" aria-expanded={open} title="Status filtern">
        {currentLabel}
      </AppButton>

      {open && createPortal(<div
        ref={menuWrapRef}
        role="menu"
        aria-label="Status wählen"
        style={{
          position: "fixed",
          top: `${pos.top}px`,
          left: `${pos.left}px`,
          width: `${pos.width}px`,
          zIndex: Z_INDEX.selectPortal
        }}
        className="w-64 rounded-xl popover-panel p-1"
      >
          {menuPanel}
        </div>, document.body)}
    </>
  );
}
function MenuItem({
  active,
  label,
  onClick
}) {
  return <AppButton type="button" variant="ghost" onClick={onClick} role="menuitemradio" aria-checked={active} className={cn("w-full !justify-start text-left rounded-lg text-sm outline-none min-h-[44px] h-auto font-normal", active ? "menu-item-active" : "menu-item-idle")}>
      {label} {active ? "•" : ""}
    </AppButton>;
}